import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { datadogRum } from '@datadog/browser-rum';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import * as yup from 'yup';

import { useCitySelection } from 'hooks/useCitySelection';
import { useSignInModalError } from 'hooks/useModalErrors';

import { getAxiosError } from 'utils/getAxiosError';
import { sendToErrorTracking } from 'utils/sendToErrorTracking';

interface FormFields {
  name: string;
  phone: string;
  email: string;
}

interface SubmitData extends FormFields {
  photos: string[];
}

const validationMessages = {
  required: 'Campo obbligatorio',
  format: 'Formato non valido',
};

const validationSchema = yup.object({
  name: yup.string().required(validationMessages.required),
  phone: yup.string().required(validationMessages.required),
  email: yup
    .string()
    .email(validationMessages.format)
    .required(validationMessages.required),
});

export const useFormSubmit = () => {
  const [loading, setLoading] = useState(false);
  const { selectedCity } = useCitySelection();
  const { setModalErrors } = useSignInModalError();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: SubmitData, callback: () => void) => {
    setLoading(true);

    datadogRum.addTiming('PresciptionModal/hooks/useFormSubmit');

    try {
      const payload = {
        ...data,
        city: selectedCity,
      };

      await axios.post(
        'https://bff-prescriptions.lamimed.it/prescriptions',
        payload,
      );

      datadogRum.addAction('PresciptionModal', {
        presciptionModal: data,
      });

      callback();
    } catch (error) {
      sendToErrorTracking({ error, where: 'useFormSubmit', context: data });

      const errorMessage = getAxiosError(error);

      if (Array.isArray(errorMessage)) {
        setModalErrors(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    Controller,
    handleSubmit,
    control,
    errors,
    onSubmit,
    loading,
  };
};
